import { useState, useEffect, useRef } from "react";
import videos, { gifs } from "../videos";
import VideoModal from "./VideoModal";

// Parse seconds from a sets string like "1 × 60 sec", "1 × 3 min", "1 × 30 sec each leg"
function parseDuration(sets) {
  const minMatch = sets.match(/(\d+)\s*min/);
  if (minMatch) return parseInt(minMatch[1]) * 60;
  const secMatch = sets.match(/(\d+)\s*sec/);
  if (secMatch) return parseInt(secMatch[1]);
  return null;
}

function isPerSide(sets) {
  return /each\s+(leg|side|arm)/i.test(sets);
}

function formatCountdown(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, "0")}` : `${s}s`;
}

function playDoneSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.25, 0.5].forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.4, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.18);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.18);
    });
  } catch {}
}

export default function ExerciseList({
  exercises,
  duration,
  note,
  hColor,
  phaseColor,
  showWakeLock,
  showCountdown,
}) {
  const [modal, setModal] = useState(null);
  const [expandedGifs, setExpandedGifs] = useState(new Set());
  const [activeIdx, setActiveIdx] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Acquire wake lock automatically when this component mounts (warmup/cooldown tab)
  useEffect(() => {
    if (!showWakeLock || !("wakeLock" in navigator)) return;
    const acquire = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch {}
    };
    acquire();
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) acquire();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [showWakeLock]);

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  function startTimer(idx, secs) {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setActiveIdx(idx);
    setRemaining(secs);
    setIsDone(false);
    intervalRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsDone(true);
          playDoneSound();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function cancelTimer() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setActiveIdx(null);
    setRemaining(0);
    setIsDone(false);
  }

  function toggleGif(i) {
    setExpandedGifs(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  const accent = hColor || phaseColor;

  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1.5px solid var(--border)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      {modal && (
        <VideoModal videoId={modal.id} exerciseName={modal.name} onClose={() => setModal(null)} />
      )}
      {note && (
        <div
          style={{
            background: "#FFFDE7",
            borderBottom: "1px solid #F0E68C",
            padding: "10px 16px",
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <span style={{ fontSize: 14, flexShrink: 0 }}>💡</span>
          <span style={{ fontSize: 12, color: "#555", fontStyle: "italic" }}>{note}</span>
        </div>
      )}
      <div
        style={{
          background: accent || "#1A1A2E",
          padding: "11px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <div style={{ color: "#fff", fontSize: 14, fontWeight: "bold" }}>{duration}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {showWakeLock && (
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span>☀</span>
              <span>Screen stays on</span>
            </div>
          )}
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>Rest 60–90s between sets</div>
        </div>
      </div>
      {exercises.map((ex, i) => {
        const gifSrc = gifs[ex.name];
        const videoId = videos[ex.name];
        const isGifExpanded = expandedGifs.has(i);
        const dur = showCountdown ? parseDuration(ex.sets) : null;
        const perSide = dur ? isPerSide(ex.sets) : false;
        const isActive = activeIdx === i;
        const running = isActive && !isDone;
        const done = isActive && isDone;

        return (
          <div
            key={i}
            style={{
              borderBottom: i < exercises.length - 1 ? `1px solid var(--border-subtle)` : "none",
              background: i % 2 === 0 ? "var(--bg-alt)" : "var(--bg-card)",
            }}
          >
            <div style={{ display: "flex", gap: 12, padding: "11px 16px" }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: accent,
                  color: "#fff",
                  fontSize: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: "bold", color: "var(--text-primary)" }}>{ex.name}</span>
                  {ex.isNew && (
                    <span
                      style={{
                        fontSize: 10,
                        background: "#FFF3E0",
                        color: "#E8650A",
                        padding: "1px 7px",
                        borderRadius: 10,
                        fontWeight: "bold",
                      }}
                    >
                      NEW
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 13,
                      color: accent,
                      fontWeight: "bold",
                      fontFamily: "monospace",
                    }}
                  >
                    {ex.sets}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--text-subtle)",
                      background: "var(--bg-subtle)",
                      padding: "1px 8px",
                      borderRadius: 10,
                    }}
                  >
                    {ex.muscles}
                  </span>
                  {gifSrc && (
                    <button
                      onClick={() => toggleGif(i)}
                      style={{
                        background: "#6C8EBF",
                        border: "none",
                        borderRadius: 4,
                        color: "#fff",
                        fontSize: 10,
                        padding: "2px 7px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      {isGifExpanded ? "▲ hide" : "▶ show"}
                    </button>
                  )}
                  {videoId && (
                    <button
                      onClick={() => setModal({ id: videoId, name: ex.name })}
                      style={{
                        background: "#FF0000",
                        border: "none",
                        borderRadius: 4,
                        color: "#fff",
                        fontSize: 10,
                        padding: "2px 7px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      ▶ video
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3, fontStyle: "italic" }}>
                  {ex.cue}
                </div>
              </div>
            </div>

            {/* Countdown timer row */}
            {dur && (
              <div style={{ padding: "0 16px 12px 52px", display: "flex", alignItems: "center", gap: 10 }}>
                {!running && !done && (
                  <button
                    onClick={() => startTimer(i, dur)}
                    style={{
                      background: accent,
                      border: "none",
                      borderRadius: 20,
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: "bold",
                      fontFamily: "monospace",
                      padding: "6px 14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    ▶ {formatCountdown(dur)}
                    {perSide && <span style={{ fontSize: 11, opacity: 0.85 }}>· each side</span>}
                  </button>
                )}
                {running && (
                  <>
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: "bold",
                        color: accent,
                        fontFamily: "monospace",
                        minWidth: 52,
                      }}
                    >
                      {formatCountdown(remaining)}
                    </div>
                    <button
                      onClick={cancelTimer}
                      style={{
                        background: "transparent",
                        border: `1px solid var(--border)`,
                        borderRadius: 16,
                        color: "var(--text-subtle)",
                        fontSize: 11,
                        padding: "4px 10px",
                        cursor: "pointer",
                      }}
                    >
                      ✕ cancel
                    </button>
                  </>
                )}
                {done && (
                  <>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: "bold",
                        color: "#27AE60",
                      }}
                    >
                      ✓ Done{perSide ? " — switch side" : ""}
                    </span>
                    <button
                      onClick={() => startTimer(i, dur)}
                      style={{
                        background: "transparent",
                        border: `1px solid ${accent}`,
                        borderRadius: 16,
                        color: accent,
                        fontSize: 11,
                        padding: "4px 10px",
                        cursor: "pointer",
                      }}
                    >
                      ↺ again
                    </button>
                  </>
                )}
              </div>
            )}

            {gifSrc && isGifExpanded && (
              <div style={{ padding: "0 16px 12px 52px" }}>
                <img
                  src={gifSrc}
                  alt={ex.name}
                  style={{
                    width: "100%",
                    maxWidth: 320,
                    borderRadius: 8,
                    border: "1px solid var(--border)",
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
